import { Controller } from "@hotwired/stimulus"
import { BREADTH_SELECT_EVENT } from "../charts/highcharts/gics_breadth_chart"

// Drill-down modal for the /market_breadth/theme_breadth boxes. Binds itself
// to the chart's breadth:select event (imported from gics_breadth_chart so the
// two sides can't drift on the name), then lazy-fetches the theme's
// constituents as an HTML fragment — server-rendered so the table's styling
// and the tier gate live in one place.
export default class extends Controller {
  static targets = ["title", "subtitle", "body", "filter", "filterCount", "rows"]
  static values = {
    url: String,
    maType: String,
    period: String
  }

  connect() {
    this.boundOpen = this.open.bind(this)
    window.addEventListener(BREADTH_SELECT_EVENT, this.boundOpen)
    this.boundBeforeCache = this.beforeCache.bind(this)
    document.addEventListener("turbo:before-cache", this.boundBeforeCache)
  }

  // Turbo snapshots the page before navigating away (e.g. via the modal's
  // "Full theme deep-dive" link). An open modal frozen into that snapshot
  // comes back on browser-back as inert markup: the restored Bootstrap
  // instance thinks it's hidden, so the close button no-ops and the stale
  // backdrop blocks the page. Close and scrub everything Bootstrap's show()
  // touched so the snapshot always holds a closed modal.
  beforeCache() {
    this.abortController?.abort()
    bootstrap.Modal.getInstance(this.element)?.dispose()
    this.element.classList.remove("show")
    this.element.style.display = ""
    this.element.setAttribute("aria-hidden", "true")
    this.element.removeAttribute("aria-modal")
    this.element.removeAttribute("role")
    document.body.classList.remove("modal-open")
    document.body.style.removeProperty("overflow")
    document.body.style.removeProperty("padding-right")
    document.querySelectorAll(".modal-backdrop").forEach(backdrop => backdrop.remove())
  }

  open(event) {
    const { entityId, name } = event.detail || {}
    if (!entityId) return

    if (this.hasTitleTarget) this.titleTarget.textContent = name || "Theme"
    // The MA name only — the fetched fragment's header owns the "N of M"
    // count, computed live server-side; repeating the chart's cached count
    // here risks the two disagreeing across a cache window.
    if (this.hasSubtitleTarget) {
      this.subtitleTarget.textContent = `${this.periodValue} ${this.maTypeValue.toUpperCase()}`
    }
    this.showSpinner()

    bootstrap.Modal.getOrCreateInstance(this.element).show()
    this.fetchConstituents(entityId)
  }

  fetchConstituents(themeId) {
    // A fresh click supersedes any in-flight fetch — without this, a slow
    // earlier response could land last and fill the modal with the wrong
    // theme's rows.
    this.abortController?.abort()
    const abortController = new AbortController()
    this.abortController = abortController

    const params = new URLSearchParams({
      theme_id: themeId,
      ma_type: this.maTypeValue,
      period: this.periodValue
    })

    fetch(`${this.urlValue}?${params}`, {
      headers: { "X-Requested-With": "XMLHttpRequest" },
      signal: abortController.signal
    })
      .then(response => {
        // An expired session 303s to the sign-in page and fetch follows it —
        // without this check the whole login page would be injected below.
        if (response.redirected) {
          throw new Error("Your session has expired — please sign in again.")
        }
        // The endpoint is Trader+; a Free user can't click a box, so a 403 here
        // means a hand-crafted request. Surface the server's upgrade message
        // rather than a bare status code.
        if (response.status === 403) {
          return response.json().then(body => {
            throw new Error(body.error || "This feature requires a paid plan.")
          })
        }
        if (!response.ok) throw new Error(`Couldn't load constituents (HTTP ${response.status})`)
        return response.text()
      })
      .then(html => {
        if (this.hasBodyTarget) this.bodyTarget.innerHTML = html
      })
      .catch(error => {
        if (error.name === "AbortError") return
        if (!this.hasBodyTarget) return
        this.bodyTarget.textContent = ""
        const alert = document.createElement("div")
        alert.className = "alert alert-danger mb-0"
        alert.textContent = error.message
        this.bodyTarget.appendChild(alert)
      })
  }

  showSpinner() {
    if (!this.hasBodyTarget) return
    this.bodyTarget.innerHTML =
      '<div class="d-flex justify-content-center py-4">' +
      '<div class="spinner-border text-primary" role="status">' +
      '<span class="visually-hidden">Loading…</span></div></div>'
  }

  // Client-side filter over the fetched rows — the biggest theme runs to a few
  // hundred stocks, and the list is already fully in the DOM.
  filter() {
    if (!this.hasFilterTarget || !this.hasRowsTarget) return

    const query = this.filterTarget.value.trim().toLowerCase()
    const rows = Array.from(this.rowsTarget.querySelectorAll("tr"))
    let visible = 0

    rows.forEach(row => {
      const haystack = row.dataset.filterText || row.textContent.toLowerCase()
      const match = query === "" || haystack.includes(query)
      row.classList.toggle("d-none", !match)
      if (match) visible += 1
    })

    if (this.hasFilterCountTarget) {
      this.filterCountTarget.classList.toggle("d-none", query === "")
      this.filterCountTarget.textContent = `${visible} of ${rows.length} shown`
    }
  }

  disconnect() {
    window.removeEventListener(BREADTH_SELECT_EVENT, this.boundOpen)
    document.removeEventListener("turbo:before-cache", this.boundBeforeCache)
    this.abortController?.abort()
    const modal = bootstrap.Modal.getInstance(this.element)
    if (modal) modal.dispose()
  }
}
